// PR-MAY15-INVESTOR-RATES-DRAWER item 2 — per-vehicle, per-site rates.
//
// Backed by /api/v1/investor-rates/vehicle (CRUD) +
// /api/v1/investor-rates/by-investor/:id (list).
//
// The list query keys on the investor id, so the InvestorVehicleRates
// panel doesn't need to know about per-row freshness — invalidating
// `['investor-vehicle-rates', investorId]` refetches the whole panel.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

// PR-MAY16-RATE-DROPDOWN-FIX — keyed on siteId.
export interface InvestorVehicleRateRow {
  id: string;
  site_id: string;
  site_name: string;
  investor_id: string;
  vehicle_id: string;
  vehicle_plate: string;
  vehicle_code: string;
  investor_rate_at_target: string;
  effective_from: string;
  effective_to: string | null;
  notes: string | null;
}

export interface InvestorVehicleRateInput {
  siteId: string;
  investorId: string;
  vehicleId: string;
  investorRateAtTarget: number;
  effectiveFrom?: string;
  effectiveTo?: string | null;
  notes?: string | null;
}

export function useInvestorVehicleRates(investorId: string | undefined) {
  return useQuery({
    queryKey: ['investor-vehicle-rates', investorId],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: InvestorVehicleRateRow[] }>(
        `/api/v1/investor-rates/by-investor/${investorId}`,
      );
      return data.items;
    },
    enabled: Boolean(investorId),
  });
}

export function useCreateInvestorVehicleRate(investorId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: InvestorVehicleRateInput) => {
      const { data } = await apiClient.post<{ rate: InvestorVehicleRateRow }>(
        '/api/v1/investor-rates/vehicle',
        input,
      );
      return data.rate;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['investor-vehicle-rates', investorId] });
    },
  });
}

export function useUpdateInvestorVehicleRate(investorId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Partial<InvestorVehicleRateInput> }) => {
      const { data } = await apiClient.patch<{ rate: InvestorVehicleRateRow }>(
        `/api/v1/investor-rates/vehicle/${id}`,
        input,
      );
      return data.rate;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['investor-vehicle-rates', investorId] });
    },
  });
}

export function useDeleteInvestorVehicleRate(investorId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete<{ deleted: boolean; id: string }>(
        `/api/v1/investor-rates/vehicle/${id}`,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['investor-vehicle-rates', investorId] });
    },
  });
}
